import type { MathTopicConfig, MathTopic, MathSubtopicConfig } from "./types";

const arithmeticSubtopics: MathSubtopicConfig[] = [
  { id: "addition", name: "Addition", description: "Single and multi-digit addition" },
  { id: "subtraction", name: "Subtraction", description: "Single and multi-digit subtraction with borrowing" },
  { id: "multiplication", name: "Multiplication", description: "Times tables, multi-digit multiplication" },
  { id: "division", name: "Division", description: "Long division, remainders" },
  { id: "fractions", name: "Fractions", description: "Operations with fractions, equivalence, simplification" },
  { id: "decimals", name: "Decimals", description: "Decimal operations, place value, conversion" },
  { id: "percentages", name: "Percentages", description: "Percentage calculations, increase/decrease" },
  { id: "ratios", name: "Ratios", description: "Ratio and proportion problems" },
  { id: "proportions", name: "Proportions", description: "Direct and inverse proportion" },
  { id: "integers", name: "Integers", description: "Operations with positive and negative numbers" },
  { id: "factors", name: "Factors & Multiples", description: "Prime factorization, LCM, GCD/HCF" },
  { id: "prime-numbers", name: "Prime Numbers", description: "Prime identification, prime factorization" },
  { id: "lcm", name: "LCM", description: "Least common multiple" },
  { id: "gcd", name: "GCD/HCF", description: "Greatest common divisor / Highest common factor" },
  { id: "number-systems", name: "Number Systems", description: "Real numbers, rational/irrational, number line" },
];

const algebraSubtopics: MathSubtopicConfig[] = [
  { id: "expressions", name: "Algebraic Expressions", description: "Simplification, evaluation, manipulation" },
  { id: "linear-equations", name: "Linear Equations", description: "One and two variable linear equations" },
  { id: "simultaneous-equations", name: "Simultaneous Equations", description: "Systems of linear equations" },
  { id: "quadratic-equations", name: "Quadratic Equations", description: "Solving, discriminant, nature of roots" },
  { id: "polynomials", name: "Polynomials", description: "Operations, factorization, remainder theorem" },
  { id: "factorisation", name: "Factorisation", description: "Common factors, grouping, special products" },
  { id: "sequences", name: "Sequences", description: "Arithmetic, geometric, recursive sequences" },
  { id: "series", name: "Series", description: "Summation, arithmetic/geometric series" },
  { id: "inequalities", name: "Inequalities", description: "Linear, quadratic, absolute value inequalities" },
  { id: "functions", name: "Functions", description: "Domain, range, composition, inverse functions" },
  { id: "logarithms", name: "Logarithms", description: "Properties, equations, applications" },
  { id: "exponents", name: "Exponents", description: "Laws of exponents, exponential equations" },
  { id: "complex-numbers", name: "Complex Numbers", description: "Operations, polar form, De Moivre's theorem" },
];

const geometrySubtopics: MathSubtopicConfig[] = [
  { id: "lines", name: "Lines & Angles", description: "Parallel, perpendicular, angle relationships" },
  { id: "angles", name: "Angles", description: "Types, measurement, angle theorems" },
  { id: "triangles", name: "Triangles", description: "Properties, congruence, similarity, Pythagorean theorem" },
  { id: "quadrilaterals", name: "Quadrilaterals", description: "Properties, area, perimeter" },
  { id: "polygons", name: "Polygons", description: "Regular polygons, interior/exterior angles" },
  { id: "circles", name: "Circles", description: "Properties, tangents, chords, arcs, sectors" },
  { id: "coordinate-geometry", name: "Coordinate Geometry", description: "Distance, midpoint, slope, line equations" },
  { id: "transformations", name: "Transformations", description: "Translation, rotation, reflection, dilation" },
  { id: "constructions", name: "Constructions", description: "Geometric constructions with compass/straightedge" },
  { id: "solid-geometry", name: "Solid Geometry", description: "3D shapes, surface area, volume" },
  { id: "vectors-geometry", name: "Vectors in Geometry", description: "Vector geometry applications" },
];

const trigonometrySubtopics: MathSubtopicConfig[] = [
  { id: "basic-trig", name: "Basic Trigonometry", description: "Sine, cosine, tangent ratios" },
  { id: "trig-identities", name: "Trigonometric Identities", description: "Pythagorean, sum/difference, double/half angle" },
  { id: "trig-equations", name: "Trigonometric Equations", description: "Solving trigonometric equations" },
  { id: "inverse-trig", name: "Inverse Trigonometry", description: "Arcsin, arccos, arctan properties" },
  { id: "trig-applications", name: "Applications", description: "Heights and distances, navigation" },
];

const calculusSubtopics: MathSubtopicConfig[] = [
  { id: "limits", name: "Limits", description: "Limit evaluation, one-sided, infinite limits" },
  { id: "continuity", name: "Continuity", description: "Continuous functions, discontinuity types" },
  { id: "differentiation", name: "Differentiation", description: "Rules, chain rule, implicit, higher derivatives" },
  { id: "diff-applications", name: "Applications of Differentiation", description: "Optimization, related rates, curve sketching" },
  { id: "integration", name: "Integration", description: "Indefinite integrals, substitution, parts, partial fractions" },
  { id: "definite-integration", name: "Definite Integration", description: "Area under curve, FTC, properties" },
  { id: "indefinite-integration", name: "Indefinite Integration", description: "Antiderivatives, integration techniques" },
  { id: "differential-equations", name: "Differential Equations", description: "First order, separable, linear, applications" },
  { id: "multivariable-calculus", name: "Multivariable Calculus", description: "Partial derivatives, multiple integrals" },
];

const statisticsSubtopics: MathSubtopicConfig[] = [
  { id: "mean", name: "Mean", description: "Arithmetic, weighted, geometric mean" },
  { id: "median", name: "Median", description: "Median for grouped/ungrouped data" },
  { id: "mode", name: "Mode", description: "Mode, modal class" },
  { id: "variance", name: "Variance", description: "Population and sample variance" },
  { id: "std-deviation", name: "Standard Deviation", description: "Calculation, interpretation" },
  { id: "probability-distributions", name: "Probability Distributions", description: "Binomial, Poisson, normal distributions" },
  { id: "correlation", name: "Correlation", description: "Pearson, Spearman correlation" },
  { id: "regression", name: "Regression", description: "Linear regression, least squares" },
  { id: "statistical-inference", name: "Statistical Inference", description: "Hypothesis testing, confidence intervals" },
];

const probabilitySubtopics: MathSubtopicConfig[] = [
  { id: "basic-probability", name: "Basic Probability", description: "Sample space, events, probability rules" },
  { id: "conditional-probability", name: "Conditional Probability", description: "P(A|B), independence, multiplication rule" },
  { id: "bayes-theorem", name: "Bayes' Theorem", description: "Applications, posterior probability" },
  { id: "random-variables", name: "Random Variables", description: "Discrete/continuous, expectation, variance" },
  { id: "discrete-probability", name: "Discrete Probability", description: "Binomial, geometric, Poisson distributions" },
  { id: "continuous-probability", name: "Continuous Probability", description: "Normal, exponential, uniform distributions" },
  { id: "combinatorial-probability", name: "Combinatorial Probability", description: "Permutations, combinations in probability" },
];

const discreteMathSubtopics: MathSubtopicConfig[] = [
  { id: "sets", name: "Sets", description: "Operations, Venn diagrams, cardinality" },
  { id: "relations", name: "Relations", description: "Properties, equivalence, partial order" },
  { id: "functions-dm", name: "Functions", description: "Injective, surjective, bijective, composition" },
  { id: "logic", name: "Logic", description: "Propositions, truth tables, logical equivalence" },
  { id: "graph-theory", name: "Graph Theory", description: "Paths, cycles, trees, planarity, coloring" },
  { id: "combinatorics", name: "Combinatorics", description: "Counting principles, P&C, inclusion-exclusion" },
  { id: "recurrence-relations", name: "Recurrence Relations", description: "Linear recurrences, generating functions" },
  { id: "number-theory-dm", name: "Number Theory", description: "Divisibility, congruences, modular arithmetic" },
  { id: "algorithms-math", name: "Algorithms", description: "Complexity, sorting, graph algorithms" },
];

const linearAlgebraSubtopics: MathSubtopicConfig[] = [
  { id: "vectors", name: "Vectors", description: "Operations, dot/cross product, applications" },
  { id: "matrices", name: "Matrices", description: "Operations, types, transpose, trace" },
  { id: "determinants", name: "Determinants", description: "Properties, evaluation, Cramer's rule" },
  { id: "systems-equations", name: "Systems of Equations", description: "Gaussian elimination, matrix inversion" },
  { id: "vector-spaces", name: "Vector Spaces", description: "Subspaces, basis, dimension, linear independence" },
  { id: "linear-transformations", name: "Linear Transformations", description: "Kernel, image, matrix representation" },
  { id: "eigenvalues", name: "Eigenvalues", description: "Characteristic polynomial, computation" },
  { id: "eigenvectors", name: "Eigenvectors", description: "Computation, diagonalization, applications" },
];

const advancedSubtopics: MathSubtopicConfig[] = [
  { id: "real-analysis", name: "Real Analysis", description: "Sequences, series, continuity, differentiability, integration" },
  { id: "complex-analysis", name: "Complex Analysis", description: "Analytic functions, contour integration, residues" },
  { id: "abstract-algebra", name: "Abstract Algebra", description: "Groups, rings, fields, homomorphisms" },
  { id: "group-theory", name: "Group Theory", description: "Subgroups, cosets, Lagrange's theorem, Sylow" },
  { id: "ring-theory", name: "Ring Theory", description: "Ideals, quotient rings, polynomial rings" },
  { id: "field-theory", name: "Field Theory", description: "Extensions, Galois theory, finite fields" },
  { id: "number-theory-adv", name: "Number Theory", description: "Prime distribution, Diophantine equations, modular forms" },
  { id: "topology", name: "Topology", description: "Metric spaces, continuity, compactness, connectedness" },
  { id: "differential-geometry", name: "Differential Geometry", description: "Curves, surfaces, curvature, geodesics" },
  { id: "functional-analysis", name: "Functional Analysis", description: "Banach/Hilbert spaces, operators, spectral theory" },
  { id: "measure-theory", name: "Measure Theory", description: "Lebesgue measure, integration, probability spaces" },
  { id: "pdes", name: "Partial Differential Equations", description: "Classification, separation of variables, Fourier methods" },
  { id: "numerical-analysis", name: "Numerical Analysis", description: "Root finding, interpolation, numerical integration" },
  { id: "math-optimization", name: "Mathematical Optimization", description: "Convex optimization, linear/nonlinear programming" },
  { id: "operations-research", name: "Operations Research", description: "Linear programming, network flows, game theory" },
  { id: "math-logic", name: "Mathematical Logic", description: "Model theory, proof theory, computability" },
  { id: "advanced-probability", name: "Advanced Probability", description: "Measure-theoretic probability, martingales, stochastic processes" },
  { id: "advanced-statistics", name: "Advanced Statistics", description: "Asymptotic theory, nonparametrics, Bayesian inference" },
];

export const mathTopics: MathTopicConfig[] = [
  {
    id: "arithmetic",
    name: "Arithmetic",
    description: "Fundamental operations with numbers",
    subtopics: arithmeticSubtopics,
    minLevel: "class-1",
    difficulties: ["easy", "medium"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "fill-in-the-blank", "true-false", "calculation", "word-problem", "mixed"],
  },
  {
    id: "algebra",
    name: "Algebra",
    description: "Variables, equations, functions, and structures",
    subtopics: algebraSubtopics,
    minLevel: "class-6",
    difficulties: ["easy", "medium", "hard"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "fill-in-the-blank", "problem-solving", "word-problem", "derivation", "calculation", "conceptual", "mixed"],
  },
  {
    id: "geometry",
    name: "Geometry",
    description: "Shapes, sizes, positions, and properties of space",
    subtopics: geometrySubtopics,
    minLevel: "class-4",
    difficulties: ["easy", "medium", "hard"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "fill-in-the-blank", "true-false", "problem-solving", "word-problem", "proof", "calculation", "conceptual", "mixed"],
  },
  {
    id: "trigonometry",
    name: "Trigonometry",
    description: "Relationships between angles and sides of triangles",
    subtopics: trigonometrySubtopics,
    minLevel: "class-10",
    difficulties: ["easy", "medium", "hard"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "fill-in-the-blank", "problem-solving", "derivation", "calculation", "conceptual", "mixed"],
  },
  {
    id: "calculus",
    name: "Calculus",
    description: "Limits, derivatives, integrals, and their applications",
    subtopics: calculusSubtopics,
    minLevel: "class-11",
    difficulties: ["easy", "medium", "hard", "expert"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "fill-in-the-blank", "problem-solving", "derivation", "calculation", "proof", "conceptual", "mixed"],
  },
  {
    id: "statistics",
    name: "Statistics",
    description: "Data collection, analysis, interpretation, and presentation",
    subtopics: statisticsSubtopics,
    minLevel: "class-8",
    difficulties: ["easy", "medium", "hard"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "fill-in-the-blank", "problem-solving", "word-problem", "calculation", "conceptual", "mixed"],
  },
  {
    id: "probability",
    name: "Probability",
    description: "Likelihood and chance of events occurring",
    subtopics: probabilitySubtopics,
    minLevel: "class-8",
    difficulties: ["easy", "medium", "hard"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "fill-in-the-blank", "true-false", "problem-solving", "word-problem", "calculation", "conceptual", "mixed"],
  },
  {
    id: "discrete-mathematics",
    name: "Discrete Mathematics",
    description: "Discrete structures: sets, logic, graphs, combinatorics",
    subtopics: discreteMathSubtopics,
    minLevel: "class-11",
    difficulties: ["medium", "hard", "expert"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "problem-solving", "proof", "derivation", "conceptual", "mixed"],
  },
  {
    id: "linear-algebra",
    name: "Linear Algebra",
    description: "Vectors, matrices, linear transformations, eigenproblems",
    subtopics: linearAlgebraSubtopics,
    minLevel: "class-12",
    difficulties: ["medium", "hard", "expert"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "problem-solving", "proof", "derivation", "calculation", "conceptual", "mixed"],
  },
  {
    id: "real-analysis",
    name: "Real Analysis",
    description: "Rigorous foundations of calculus",
    subtopics: advancedSubtopics.filter(s => s.id === "real-analysis"),
    minLevel: "undergraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
  {
    id: "complex-analysis",
    name: "Complex Analysis",
    description: "Functions of a complex variable",
    subtopics: advancedSubtopics.filter(s => s.id === "complex-analysis"),
    minLevel: "undergraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
  {
    id: "abstract-algebra",
    name: "Abstract Algebra",
    description: "Algebraic structures: groups, rings, fields",
    subtopics: advancedSubtopics.filter(s => ["abstract-algebra", "group-theory", "ring-theory", "field-theory"].includes(s.id)),
    minLevel: "undergraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
  {
    id: "number-theory",
    name: "Number Theory",
    description: "Properties and relationships of integers",
    subtopics: advancedSubtopics.filter(s => s.id === "number-theory-adv"),
    minLevel: "undergraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "problem-solving", "conceptual"],
  },
  {
    id: "topology",
    name: "Topology",
    description: "Properties preserved under continuous deformations",
    subtopics: advancedSubtopics.filter(s => s.id === "topology"),
    minLevel: "postgraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "conceptual"],
  },
  {
    id: "differential-geometry",
    name: "Differential Geometry",
    description: "Geometry of curves and surfaces using calculus",
    subtopics: advancedSubtopics.filter(s => s.id === "differential-geometry"),
    minLevel: "postgraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
  {
    id: "functional-analysis",
    name: "Functional Analysis",
    description: "Infinite-dimensional vector spaces and operators",
    subtopics: advancedSubtopics.filter(s => s.id === "functional-analysis"),
    minLevel: "postgraduate",
    difficulties: ["expert"],
    questionTypes: ["proof", "derivation", "conceptual"],
  },
  {
    id: "measure-theory",
    name: "Measure Theory",
    description: "Foundations of probability and integration",
    subtopics: advancedSubtopics.filter(s => s.id === "measure-theory"),
    minLevel: "postgraduate",
    difficulties: ["expert"],
    questionTypes: ["proof", "derivation", "conceptual"],
  },
  {
    id: "partial-differential-equations",
    name: "Partial Differential Equations",
    description: "Equations involving partial derivatives",
    subtopics: advancedSubtopics.filter(s => s.id === "pdes"),
    minLevel: "postgraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
  {
    id: "numerical-analysis",
    name: "Numerical Analysis",
    description: "Algorithms for numerical approximation",
    subtopics: advancedSubtopics.filter(s => s.id === "numerical-analysis"),
    minLevel: "undergraduate",
    difficulties: ["medium", "hard", "expert"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "problem-solving", "derivation", "calculation", "conceptual"],
  },
  {
    id: "mathematical-optimization",
    name: "Mathematical Optimization",
    description: "Finding optimal solutions under constraints",
    subtopics: advancedSubtopics.filter(s => s.id === "math-optimization"),
    minLevel: "undergraduate",
    difficulties: ["medium", "hard", "expert"],
    questionTypes: ["multiple-choice", "short-answer", "problem-solving", "derivation", "conceptual"],
  },
  {
    id: "operations-research",
    name: "Operations Research",
    description: "Mathematical methods for decision making",
    subtopics: advancedSubtopics.filter(s => s.id === "operations-research"),
    minLevel: "undergraduate",
    difficulties: ["medium", "hard", "expert"],
    questionTypes: ["multiple-choice", "short-answer", "problem-solving", "derivation", "conceptual"],
  },
  {
    id: "mathematical-logic",
    name: "Mathematical Logic",
    description: "Formal systems, proof theory, computability",
    subtopics: advancedSubtopics.filter(s => s.id === "math-logic"),
    minLevel: "postgraduate",
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "conceptual"],
  },
  {
    id: "advanced-probability",
    name: "Advanced Probability",
    description: "Measure-theoretic probability and stochastic processes",
    subtopics: advancedSubtopics.filter(s => s.id === "advanced-probability"),
    minLevel: "postgraduate",
    difficulties: ["expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
  {
    id: "advanced-statistics",
    name: "Advanced Statistics",
    description: "Statistical theory and inference",
    subtopics: advancedSubtopics.filter(s => s.id === "advanced-statistics"),
    minLevel: "postgraduate",
    difficulties: ["expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
];

export function getMathTopic(id: string): MathTopicConfig | undefined {
  return mathTopics.find((t) => t.id === id);
}

const LEVEL_ORDER: readonly string[] = [
  "class-1", "class-2", "class-3", "class-4", "class-5",
  "class-6", "class-7", "class-8", "class-9", "class-10",
  "class-11", "class-12", "jee-foundation", "jee-main", "jee-advanced",
  "neet", "state-competitive", "elementary", "middle-school",
  "grade-9", "grade-10", "grade-11", "grade-12", "ap-level",
  "college", "undergraduate", "graduate", "postgraduate", "advanced",
];

export function getTopicsForLevel(level: string): MathTopicConfig[] {
  return mathTopics.filter((t) => {
    const topicIdx = LEVEL_ORDER.indexOf(t.minLevel);
    const levelIdx = LEVEL_ORDER.indexOf(level);
    return levelIdx >= 0 && levelIdx >= topicIdx;
  });
}

export function getTopicNames(): { id: MathTopic; name: string }[] {
  return mathTopics.map((t) => ({ id: t.id, name: t.name }));
}